import React, { useState, useRef } from 'react';
import { RotateCcw } from 'lucide-react';

const SimuladorMamoplastia = () => {
  const [valorEquipe, setValorEquipe] = useState('');
  const [valorHospital, setValorHospital] = useState('');
  const [valorImplante, setValorImplante] = useState('');
  const [parcelas, setParcelas] = useState('1');
  const printRef = useRef(null);

  const taxas = {
    1: 4.27, 2: 5.93, 3: 6.67, 4: 7.47, 5: 8.29, 6: 9.16,
    7: 10.93, 8: 11.80, 9: 12.68, 10: 13.63, 11: 14.54, 12: 15.58,
    13: 17.31, 14: 18.33, 15: 19.41, 16: 20.56, 17: 21.64, 18: 22.78,
    19: 24.21, 20: 25.36, 21: 26.54, 22: 27.73, 23: 28.95, 24: 30.20
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const parseMoeda = (valor) => {
    return parseFloat(valor.replace(/[^\d,]/g, '').replace(',', '.')) || 0;
  };

  const handleInputChange = (setter) => (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value) {
      value = (parseInt(value) / 100).toFixed(2);
      value = formatarMoeda(parseFloat(value));
    }
    setter(value);
  };

  const calcularTotal = () => {
    const equipe = parseMoeda(valorEquipe);
    // hospital e implante ocultos temporariamente; mantidos para futura reversão
    const hospital = parseMoeda(valorHospital);
    const implante = parseMoeda(valorImplante);
    return equipe + hospital + implante;
  };

  const calcularTotalComJuros = () => {
    const total = calcularTotal();
    const taxa = taxas[parseInt(parcelas)] || 0;
    return Math.ceil(total * (1 + taxa / 100));
  };

  const calcularParcela = () => {
    const totalComJuros = calcularTotalComJuros();
    return totalComJuros / parseInt(parcelas);
  };

  const limparCampos = () => {
    setValorEquipe('');
    setValorHospital('');
    setValorImplante('');
    setParcelas('1');
  };

  const totalBase = calcularTotal();
  const totalFinal = calcularTotalComJuros();
  const valorParcela = calcularParcela();
  const mostrarResultados = totalBase > 0;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f5f0 0%, #ffffff 100%)',
      padding: 'clamp(1rem, 3vw, 2rem)',
      fontFamily: "'Poppins', -apple-system, sans-serif"
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');
        
        * {
          box-sizing: border-box;
        }

        /* Modo de impressão: apenas remove blur/efeitos que atrapalham */
        .print-mode {
          filter: none !important;
          opacity: 1 !important;
        }

        .print-mode * {
          box-shadow: none !important;
          text-shadow: none !important;
        }

        .print-mode .decorative-blur {
          display: none !important;
        }
        
        @media (min-width: 768px) {
          .responsive-padding {
            padding: 2rem !important;
          }
        }
        
        @media (max-width: 767px) {
          .responsive-title {
            font-size: 2rem !important;
            line-height: 1.1 !important;
          }
          .responsive-subtitle {
            font-size: 1rem !important;
          }
          .responsive-parcela {
            font-size: 2.5rem !important;
          }
          .responsive-card {
            padding: 1.25rem !important;
            border-radius: 14px !important;
          }
          .responsive-grid {
            grid-template-columns: 1fr !important;
          }
          .responsive-results {
            padding: 1.25rem !important;
            border-radius: 14px !important;
          }
          input, select {
            font-size: 0.9rem !important;
            border-radius: 10px !important;
            padding: 0.78rem 0.95rem !important;
          }
          button {
            font-size: 0.875rem !important;
            padding: 0.8rem !important;
            border-radius: 10px !important;
          }
          .logo-compact {
            max-height: 28px !important;
          }
          .header-wrap {
            margin-bottom: 1rem !important;
          }
        }
        
        @media (min-width: 768px) and (max-width: 1024px) {
          .responsive-title {
            font-size: 2.5rem !important;
          }
          .responsive-parcela {
            font-size: 3.5rem !important;
          }
          .responsive-card {
            padding: 2rem !important;
          }
          .responsive-results {
            padding: 2rem !important;
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-fade-in {
          animation: fadeInUp 0.6s ease-out forwards;
        }
        
        .animate-scale {
          animation: scaleIn 0.5s ease-out forwards;
        }
        
        input:focus, select:focus {
          outline: none;
          border-color: #757c55 !important;
          box-shadow: 0 0 0 3px rgba(117, 124, 85, 0.1) !important;
        }
        
        button {
          transition: all 0.3s ease;
        }
        
        button:hover {
          transform: translateY(-2px);
        }
        
        button:active {
          transform: translateY(0);
        }
      `}</style>

      <div ref={printRef} className="print-area" style={{
        maxWidth: '900px',
        margin: '0 auto',
        width: '100%'
      }}>
        {/* Header */}
        <div className="animate-fade-in header-wrap" style={{
          textAlign: 'center',
          marginBottom: 'clamp(1rem, 2.5vw, 1.5rem)'
        }}>
          <div style={{ marginBottom: '0.65rem' }}>
            <img
              src="/LOGO-R.png"
              alt="Logo"
              className="logo-compact"
              style={{
                maxHeight: 'clamp(40px, 12vw, 52px)',
                width: 'auto'
              }}
            />
          </div>
          <h1 className="responsive-title" style={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: '2.6rem',
            fontWeight: '700',
            color: '#757c55',
            margin: '0',
            letterSpacing: '-0.5px',
            lineHeight: 1.15
          }}>
            Simulação Financiamento
          </h1>
        </div>

        {/* Main Card */}
        <div className="animate-scale responsive-card" style={{
          background: 'white',
          borderRadius: '24px',
          padding: '2.1rem',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 10px 15px -3px rgba(0, 0, 0, 0.05)',
          border: '1px solid rgba(117, 124, 85, 0.08)'
        }}>
          {/* Input Section */}
          <div style={{
            display: 'grid',
            gap: 'clamp(0.75rem, 1.5vw, 1.25rem)',
            marginBottom: '1.5rem'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#757c55',
                marginBottom: '0.5rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Valor
              </label>
              <input
                type="text"
                value={valorEquipe}
                onChange={handleInputChange(setValorEquipe)}
                placeholder="R$ 0,00"
                style={{
                  width: '100%',
                  padding: '0.85rem 1.05rem',
                  fontSize: '1.02rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '11px',
                  transition: 'all 0.2s',
                  fontWeight: '500',
                  color: '#1f2937'
                }}
              />
            </div>

            <div style={{
              marginTop: '-0.25rem',
              padding: '0.5rem 0.75rem',
              borderRadius: '10px',
              background: 'rgba(117, 124, 85, 0.06)',
              color: '#5f6844',
              fontWeight: 600,
              fontSize: '0.95rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>Total</span>
              <span>{formatarMoeda(calcularTotal())}</span>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#757c55',
                marginBottom: '0.5rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Quantidade de Parcelas
              </label>
              <select
                value={parcelas}
                onChange={(e) => setParcelas(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.85rem 1.05rem',
                  fontSize: '1.02rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '11px',
                  transition: 'all 0.2s',
                  fontWeight: '500',
                  color: '#1f2937',
                  cursor: 'pointer',
                  background: 'white'
                }}
              >
                {Object.keys(taxas).map(num => (
                  <option key={num} value={num}>
                    {num}x Crédito
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Section */}
          {mostrarResultados && (
            <div className="responsive-results" style={{
              background: 'linear-gradient(135deg, #757c55 0%, #8a9168 100%)',
              borderRadius: '20px',
              padding: '2rem',
              marginBottom: '1.5rem',
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.4rem'
                }}>
                  <p style={{
                    margin: 0,
                    fontSize: '1rem',
                    fontWeight: '600',
                    letterSpacing: '0.5px',
                    opacity: 0.9
                  }}>
                    Em <span style={{ fontWeight: 700 }}>{parcelas}x</span> de
                  </p>
                  <p className="responsive-parcela" style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '3.2rem',
                    fontWeight: '700',
                    margin: 0,
                    lineHeight: 1.05,
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    {formatarMoeda(valorParcela)}
                  </p>
                  <p style={{
                    margin: 0,
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    color: 'rgba(255,255,255,0.9)',
                    opacity: 0.9
                  }}>
                    Total = {formatarMoeda(totalFinal)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="no-print responsive-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '0.85rem'
          }}>
            <button
              onClick={limparCampos}
              style={{
                padding: '1rem',
                fontSize: '1rem',
                fontWeight: '600',
                border: '2px solid #d0d18f',
                background: 'white',
                color: '#757c55',
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <RotateCcw size={20} />
              Limpar
            </button>

          </div>
        </div>

      </div>
    </div>
  );
};

export default SimuladorMamoplastia;